//components\MedicalHistory_Section.js
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import Modal, { ModalCloseButton } from './ui/Modal';

const MedicalHistorySection = ({
  allMedicalHistory,
  setAllMedicalHistory,
  pmrn,
  clinicianId,
  API_BASE_URL,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    diagnosis: '',
    Past_Medical_History: '',
    Allergies: '',
    Last_Hospitalization: '',
    clinicalNotes: '',
    id: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  // FIX: useCallback to avoid ESLint warning
  const fetchMedicalHistory = useCallback(async () => {
    if (!pmrn) return;
    try {
      const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
      const data = await res.json();
      
      // Filter out records that have Additional_Notes (those are for other page)
      const filteredData = data.filter(record => 
        !record.Additional_Notes || record.Additional_Notes.trim() === ''
      );
      
      setAllMedicalHistory(filteredData);
    } catch (err) {
      console.error('Failed to fetch medical history:', err);
    }
  }, [API_BASE_URL, pmrn, setAllMedicalHistory]);

  useEffect(() => {
//	console.log("API_BASE_URL in MedicalHistorySection:", API_BASE_URL);

    fetchMedicalHistory();
  }, [fetchMedicalHistory]); // safe dependency

  const handleAdd = () => {
    setFormData({
      diagnosis: '',
      Past_Medical_History: '',
      Allergies: '',
      Last_Hospitalization: '',
      clinicalNotes: '',
      id: null,
    });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setFormData({ ...record });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (recordId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/medical-history/${recordId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Delete failed');
      setDeleteTarget(null);
      fetchMedicalHistory();
    } catch (err) {
      console.error('Error deleting medical history:', err);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      diagnosis: formData.diagnosis,
      Past_Medical_History: formData.Past_Medical_History,
      Allergies: formData.Allergies,
      Last_Hospitalization: formData.Last_Hospitalization,
      clinicalNotes: formData.clinicalNotes,
      PMRN: pmrn,
      updatedByClinicianId: clinicianId,
      // Ensure Additional_Notes is empty for this page's records
      Additional_Notes: null,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/medical-history/${isEditing ? `/${formData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error('Request failed');
      setIsModalOpen(false);
      fetchMedicalHistory();
    } catch (err) {
      console.error('Error saving medical history:', err);
    }
  };

  // Define the field labels and corresponding keys
  const fields = [
    { label: 'Diagnosis', key: 'diagnosis' },
    { label: 'Past Medical History', key: 'Past_Medical_History' },
    { label: 'Allergies', key: 'Allergies' },
    { label: 'Last Hospitalization', key: 'Last_Hospitalization' },
    { label: 'Clinical Notes', key: 'clinicalNotes' },
  ];

  return (
    <div className="border border-teal-300 rounded-lg shadow-sm bg-white p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-teal-300 pb-2">
        <h3 className="font-semibold text-teal-700 text-lg">Medical History</h3>
        <button
          onClick={handleAdd}
          title="Add"
          className="p-1 rounded hover:bg-teal-100 transition"
          aria-label="Add clinical finding"
        >
          <Plus size={20} color="#0d9488" />
        </button>
      </div>

      {/* Vertical Table Layout (Fields as rows, Records as columns) */}
      <div className="overflow-x-auto">
        <table className="table-auto border w-full text-sm text-gray-700">
          <tbody>
            {fields.map(({ label, key }) => (
              <tr key={key} className="border-b">
                <td className="font-semibold text-teal-800 py-2 px-2 border-r w-1/4 bg-teal-50">
                  {label}
                </td>
                {allMedicalHistory.length > 0 ? (
                  allMedicalHistory.map((record) => (
                    <td key={`${record.id}-${key}`} className="py-2 px-2 border-r text-gray-700">
                      {record[key] || '—'}
                    </td>
                  ))
                ) : (
                  <td className="py-2 px-2 text-gray-400 italic">—</td>
                )}
              </tr>
            ))}

            {/* Edit/Delete Row */}
            {allMedicalHistory.length > 0 && (
              <tr>
                <td className="font-semibold text-teal-800 py-2 px-2 border-r bg-teal-50">
                  Actions
                </td>
                {allMedicalHistory.map((record) => (
                  <td key={`action-${record.id}`} className="py-2 px-2 flex gap-2 justify-center">
                    <button
                      onClick={() => handleEdit(record)}
                      className="text-teal-600 hover:bg-teal-100 p-1 rounded"
                      title="Edit"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(record)}
                      className="text-red-500 hover:bg-red-100 p-1 rounded"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="bg-white p-6 rounded-lg w-full max-w-md space-y-4 shadow-lg">
            <div className="flex justify-between items-center border-b pb-2">
              <h2 className="text-lg font-semibold text-teal-700">
                {isEditing ? 'Edit Medical History' : 'Add Medical History'}
              </h2>
              <ModalCloseButton onClose={() => setIsModalOpen(false)} />
            </div>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Diagnosis"
                className="w-full border p-2 rounded"
                value={formData.diagnosis}
                onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
              />
              <input
                type="text"
                placeholder="Past Medical History"
                className="w-full border p-2 rounded"
                value={formData.Past_Medical_History}
                onChange={(e) => setFormData({ ...formData, Past_Medical_History: e.target.value })}
              />
              <input
                type="text"
                placeholder="Allergies"
                className="w-full border p-2 rounded"
                value={formData.Allergies}
                onChange={(e) => setFormData({ ...formData, Allergies: e.target.value })}
              />
              <input
                type="text"
                placeholder="Last Hospitalization"
                className="w-full border p-2 rounded"
                value={formData.Last_Hospitalization}
                onChange={(e) => setFormData({ ...formData, Last_Hospitalization: e.target.value })}
              />
              <textarea
                placeholder="Clinical Notes"
                className="w-full border p-2 rounded h-20"
                value={formData.clinicalNotes}
                onChange={(e) => setFormData({ ...formData, clinicalNotes: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700"
              >
                {isEditing ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-red-600">Delete Record</h3>
              <ModalCloseButton onClose={() => setDeleteTarget(null)} />
            </div>
            <p className="text-gray-700">
              Are you sure you want to delete this medical history record?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget.id)}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
      </Modal>
    </div>
  );
};

export default MedicalHistorySection;




// //components\MedicalHistory_Section.js

// import React, { useState, useEffect, useCallback } from 'react';
// import { Pencil, Plus, Trash2, X } from 'lucide-react';

// const MedicalHistorySection = ({
//   allMedicalHistory,
//   setAllMedicalHistory,
//   pmrn,
//   clinicianId,
//   API_BASE_URL,
// }) => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [formData, setFormData] = useState({
//     diagnosis: '',
//     Past_Medical_History: '',
//     Allergies: '',
//     Last_Hospitalization: '',
//     clinicalNotes: '',
//     id: null,
//   });
//   const [deleteTarget, setDeleteTarget] = useState(null);

//   // ✅ FIX: useCallback to avoid ESLint warning
//   const fetchMedicalHistory = useCallback(async () => {
//     if (!pmrn) return;
//     try {
//       const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
//       const data = await res.json();
//       setAllMedicalHistory(data);
//     } catch (err) {
//       console.error('Failed to fetch medical history:', err);
//     }
//   }, [API_BASE_URL, pmrn, setAllMedicalHistory]);

//   useEffect(() => {
//     fetchMedicalHistory();
//   }, [fetchMedicalHistory]); // ✅ safe dependency

//   const handleAdd = () => {
//     setFormData({
//       diagnosis: '',
//       Past_Medical_History: '',
//       Allergies: '',
//       Last_Hospitalization: '',
//       clinicalNotes: '',
//       id: null,
//     });
//     setIsEditing(false);
//     setIsModalOpen(true);
//   };

//   const handleEdit = (record) => {
//     setFormData({ ...record });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleDelete = async (recordId) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/medical-history/${recordId}`, {
//         method: 'DELETE',
//       });
//       if (!response.ok) throw new Error('Delete failed');
//       setDeleteTarget(null);
//       fetchMedicalHistory();
//     } catch (err) {
//       console.error('Error deleting medical history:', err);
//     }
//   };

//   const handleSubmit = async () => {
//     const payload = {
//       diagnosis: formData.diagnosis,
//       Past_Medical_History: formData.Past_Medical_History,
//       Allergies: formData.Allergies,
//       Last_Hospitalization: formData.Last_Hospitalization,
//       clinicalNotes: formData.clinicalNotes,
//       PMRN: pmrn,
//       updatedByClinicianId: clinicianId,
//     };

//     try {
//       const response = await fetch(
//         `${API_BASE_URL}/medical-history${isEditing ? `/${formData.id}` : ''}`,
//         {
//           method: isEditing ? 'PUT' : 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error('Request failed');

//       setIsModalOpen(false);
//       fetchMedicalHistory();
//     } catch (err) {
//       console.error('Error saving medical history:', err);
//     }
//   };

//   // Define the field labels and corresponding keys
//   const fields = [
//     { label: 'Diagnosis', key: 'diagnosis' },
//     { label: 'Past Medical History', key: 'Past_Medical_History' },
//     { label: 'Allergies', key: 'Allergies' },
//     { label: 'Last Hospitalization', key: 'Last_Hospitalization' },
//     { label: 'Clinical Notes', key: 'clinicalNotes' },
//   ];

//   return (
//     <div className="border border-teal-300 rounded-lg shadow-sm bg-white p-4 space-y-4">
//       {/* Header */}
//       <div className="flex justify-between items-center border-b border-teal-300 pb-2">
//         <h3 className="font-semibold text-teal-700 text-lg">Medical History</h3>
//         <button
//           onClick={handleAdd}
//           title="Add New"
//           className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-md transition"
//         >
//           <Plus size={16} />
//           Add
//         </button>
//       </div>

//       {/* Vertical Table Layout (Fields as rows, Records as columns) */}
//       <div className="overflow-x-auto">
//         <table className="table-auto border w-full text-sm text-gray-700">
//           <tbody>
//             {fields.map(({ label, key }) => (
//               <tr key={key} className="border-b">
//                 <td className="font-semibold text-teal-800 py-2 px-2 border-r w-1/4 bg-teal-50">
//                   {label}
//                 </td>
//                 {allMedicalHistory.length > 0 ? (
//                   allMedicalHistory.map((record) => (
//                     <td key={`${record.id}-${key}`} className="py-2 px-2 border-r text-gray-700">
//                       {record[key] || '—'}
//                     </td>
//                   ))
//                 ) : (
//                   <td className="py-2 px-2 text-gray-400 italic">—</td>
//                 )}
//               </tr>
//             ))}

//             {/* Edit/Delete Row */}
//             {allMedicalHistory.length > 0 && (
//               <tr>
//                 <td className="font-semibold text-teal-800 py-2 px-2 border-r bg-teal-50">
//                   Actions
//                 </td>
//                 {allMedicalHistory.map((record) => (
//                   <td key={`action-${record.id}`} className="py-2 px-2 flex gap-2 justify-center">
//                     <button
//                       onClick={() => handleEdit(record)}
//                       className="text-teal-600 hover:bg-teal-100 p-1 rounded"
//                       title="Edit"
//                     >
//                       <Pencil size={18} />
//                     </button>
//                     <button
//                       onClick={() => setDeleteTarget(record)}
//                       className="text-red-500 hover:bg-red-100 p-1 rounded"
//                       title="Delete"
//                     >
//                       <Trash2 size={18} />
//                     </button>
//                   </td>
//                 ))}
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Add/Edit Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg w-full max-w-md space-y-4 shadow-lg">
//             <div className="flex justify-between items-center border-b pb-2">
//               <h2 className="text-lg font-semibold text-teal-700">
//                 {isEditing ? 'Edit Medical History' : 'Add Medical History'}
//               </h2>
//               <button onClick={() => setIsModalOpen(false)}>
//                 <X size={20} className="text-gray-500 hover:text-gray-700" />
//               </button>
//             </div>

//             <div className="space-y-3">
//               <input
//                 type="text"
//                 placeholder="Diagnosis"
//                 className="w-full border p-2 rounded"
//                 value={formData.diagnosis}
//                 onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
//               />
//               <input
//                 type="text"
//                 placeholder="Past Surgeries"
//                 className="w-full border p-2 rounded"
//                 value={formData.Past_Medical_History}
//                 onChange={(e) => setFormData({ ...formData, Past_Medical_History: e.target.value })}
//               />
//               <input
//                 type="text"
//                 placeholder="Family History"
//                 className="w-full border p-2 rounded"
//                 value={formData.Allergies}
//                 onChange={(e) => setFormData({ ...formData, Allergies: e.target.value })}
//               />
//               <input
//                 type="text"
//                 placeholder="Last_Hospitalization"
//                 className="w-full border p-2 rounded"
//                 value={formData.Last_Hospitalization}
//                 onChange={(e) => setFormData({ ...formData, Last_Hospitalization: e.target.value })}
//               />
//               <textarea
//                 placeholder="Clinical Notes"
//                 className="w-full border p-2 rounded h-20"
//                 value={formData.clinicalNotes}
//                 onChange={(e) => setFormData({ ...formData, clinicalNotes: e.target.value })}
//               />
//             </div>

//             <div className="flex justify-end gap-3 pt-3 border-t">
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSubmit}
//                 className="px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700"
//               >
//                 {isEditing ? 'Update' : 'Add'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {deleteTarget && (
//         <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full space-y-4">
//             <h3 className="text-lg font-semibold text-red-600">Delete Record</h3>
//             <p className="text-gray-700">
//               Are you sure you want to delete this medical history record?
//             </p>
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={() => setDeleteTarget(null)}
//                 className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleDelete(deleteTarget.id)}
//                 className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MedicalHistorySection;







// import React, { useState, useEffect } from 'react';
// import { Pencil, Plus } from 'lucide-react';

// const MedicalHistorySection = ({ allMedicalHistory, setAllMedicalHistory, pmrn, clinicianId, API_BASE_URL }) => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [formData, setFormData] = useState({
//     diagnosis: '',
//     pastSurgeries: '',
//     familyHistory: '',
//     hospitalization: '',
//     clinicalNotes: '',
//     id: null,
//   });

//   const fetchMedicalHistory = async () => {
//     if (!pmrn) return;
//     try {
//       const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
//       const data = await res.json();
//       setAllMedicalHistory(data);
//     } catch (err) {
//       console.error('Failed to fetch medical history:', err);
//     }
//   };

//   useEffect(() => {
//     fetchMedicalHistory();
//   }, [pmrn]);

//   const handleAdd = () => {
//     setFormData({
//       diagnosis: '',
//       pastSurgeries: '',
//       familyHistory: '',
//       hospitalization: '',
//       clinicalNotes: '',
//       id: null,
//     });
//     setIsEditing(false);
//     setIsModalOpen(true);
//   };

//   const handleEdit = (record) => {
//     setFormData({
//       diagnosis: record.diagnosis || '',
//       pastSurgeries: record.pastSurgeries || '',
//       familyHistory: record.familyHistory || '',
//       hospitalization: record.hospitalization || '',
//       clinicalNotes: record.clinicalNotes || '',
//       id: record.id,
//     });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleSubmit = async () => {
//     const payload = {
//       diagnosis: formData.diagnosis,
//       pastSurgeries: formData.pastSurgeries,
//       familyHistory: formData.familyHistory,
//       hospitalization: formData.hospitalization,
//       clinicalNotes: formData.clinicalNotes,
//       PMRN: pmrn,
//       updatedByClinicianId: clinicianId,
//     };

//     try {
//       const response = await fetch(
//         `${API_BASE_URL}/medical-history${isEditing ? `/${formData.id}` : ''}`,
//         {
//           method: isEditing ? 'PUT' : 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error('Request failed');

//       setIsModalOpen(false);
//       fetchMedicalHistory();
//     } catch (err) {
//       console.error('Error saving medical history:', err);
//     }
//   };

//   // Field Labels and Field Keys
//   const fields = [
//     { label: 'Diagnosis', key: 'diagnosis' },
//     { label: 'Past Surgeries', key: 'pastSurgeries' },
//     { label: 'Family History', key: 'familyHistory' },
//     { label: 'Hospitalization', key: 'hospitalization' },
//     { label: 'Clinical Notes', key: 'clinicalNotes' },
//   ];

//   return (
//     <div className="border border-teal-300 rounded p-4 space-y-4">
//       <div className="flex justify-between items-center border-b border-teal-300 pb-2">
//         <h3 className="font-semibold text-teal-700">Medical History</h3>
//         <button
//           onClick={handleAdd}
//           title="Add"
//           className="p-1 rounded hover:bg-teal-100 transition"
//           aria-label="Add medical history"
//         >
//           <Plus size={20} color="#0d9488" />
//         </button>
//       </div>

//       {/* Vertical Table Layout */}
//       <div className="overflow-x-auto">
//         <table className="table-auto border w-full text-sm text-gray-700">
//           <tbody>
//             {fields.map(({ label, key }) => (
//               <tr key={key} className="border-b">
//                 <td className="font-semibold text-teal-800 py-2 px-2 border-r w-1/5">{label}</td>
//                 {allMedicalHistory.length > 0 ? (
//                   allMedicalHistory.map((record) => (
//                     <td key={record.id + key} className="py-2 px-2 border-r w-1/5">
//                       {record[key] || '-'}
//                     </td>
//                   ))
//                 ) : (
//                   <td className="py-2 px-2 text-gray-400">—</td>
//                 )}
//               </tr>
//             ))}

//             {/* Action buttons if data exists */}
//             {allMedicalHistory.length > 0 && (
//               <tr>
//                 <td className="font-semibold text-teal-800 py-2 px-2 border-r">Edit</td>
//                 {allMedicalHistory.map((record) => (
//                   <td key={`action-${record.id}`} className="py-2 px-2">
//                     <button
//                       onClick={() => handleEdit(record)}
//                       className="p-1 rounded hover:bg-teal-100 transition"
//                       title="Edit"
//                     >
//                       <Pencil size={18} color="#0d9488" />
//                     </button>
//                   </td>
//                 ))}
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg w-96 space-y-4">
//             <h2 className="text-lg font-semibold">{isEditing ? 'Edit' : 'Add'} Medical History</h2>
//             <input
//               type="text"
//               placeholder="Diagnosis"
//               className="w-full border p-2"
//               value={formData.diagnosis}
//               onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
//             />
//             <input
//               type="text"
//               placeholder="Past Medical History"
//               className="w-full border p-2"
//               value={formData.pastSurgeries}
//               onChange={(e) => setFormData({ ...formData, pastSurgeries: e.target.value })}
//             />
//             <input
//               type="text"
//               placeholder="Allergies"
//               className="w-full border p-2"
//               value={formData.familyHistory}
//               onChange={(e) => setFormData({ ...formData, familyHistory: e.target.value })}
//             />
//             <input
//               type="text"
//               placeholder="Last Hospitalization"
//               className="w-full border p-2"
//               value={formData.hospitalization}
//               onChange={(e) => setFormData({ ...formData, hospitalization: e.target.value })}
//             />
//             <textarea
//               placeholder="Clinical Notes"
//               className="w-full border p-2"
//               value={formData.clinicalNotes}
//               onChange={(e) => setFormData({ ...formData, clinicalNotes: e.target.value })}
//             />
//             <div className="flex justify-end space-x-2">
//               <button onClick={() => setIsModalOpen(false)} className="text-gray-500">Cancel</button>
//               <button onClick={handleSubmit} className="bg-teal-500 text-white px-4 py-2 rounded">
//                 {isEditing ? 'Update' : 'Add'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MedicalHistorySection;
